import { useState } from 'react';
import {
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DataTableProps } from '@/lib/types';
import { SortableHeader } from './sortable-header';

export function DataTable<TData>({
  data,
  columns,
  pageSize = 5,
  onRowClick,
  actionButtonText,
  onActionButtonClick,
  initialSorting,
  variant = 'bordered',
  emptyState,
  emptyIcon: EmptyIcon,
  emptyTitle,
  emptyDescription,
  footerContent,
  getRowClassName,
  getRowId,
}: DataTableProps<TData> & { getRowId?: (row: TData, index: number) => string }) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    // Optional stable row id — without this, TanStack uses the array index,
    // which causes React to reuse DOM rows with new data on delete. Cells
    // that hold local useState (e.g. inline edit ``localVal``) then show
    // stale text and the last DOM row appears to be the one removed. #4625.
    getRowId,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  const isFlush = variant === 'flush';
  const wrapperClassName = isFlush
    ? '-mx-6 -mb-6 overflow-x-auto rounded-b-lg'
    : 'bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden';

  if (data.length === 0 && EmptyIcon && emptyTitle) {
    return (
      <div className='border rounded-lg bg-white overflow-hidden'>
        <div className='flex flex-col items-center justify-center py-16 px-6 text-center'>
          <EmptyIcon className='h-12 w-12 text-gray-500 mb-4' strokeWidth={1} />
          <h3 className='text-lg font-semibold text-gray-500 mb-2'>{emptyTitle}</h3>
          {emptyDescription && <p className='text-gray-500 max-w-sm'>{emptyDescription}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClassName}>
      <div className={isFlush ? '' : 'overflow-x-auto'}>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-white'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const metaClassName = (header.column.columnDef.meta as { className?: string } | undefined)?.className;
                  return (
                  <th key={header.id} colSpan={header.colSpan} className={cn('py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider', metaClassName ?? 'px-6')}>
                    {header.isPlaceholder ? null : (
                      typeof header.column.columnDef.header === 'string' && header.column.getCanSort() ? (
                        <SortableHeader column={header.column}>
                          {header.column.columnDef.header}
                        </SortableHeader>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )
                    )}
                  </th>
                  );
                })}
                {actionButtonText && onActionButtonClick && (
                  <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                    <Button
                      onClick={onActionButtonClick}
                      variant='secondary'
                      className='text-white bg-[#290BD9] hover:bg-[#2309C5]'
                    >
                      {actionButtonText}
                    </Button>
                  </th>
                )}
              </tr>
            ))}
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onRowClick?.(row.original)}
                  className={cn(
                    onRowClick ? 'cursor-pointer hover:bg-gray-50' : 'hover:bg-gray-50',
                    getRowClassName?.(row.original),
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const metaClassName = (cell.column.columnDef.meta as { className?: string } | undefined)?.className;
                    return (
                    <td key={cell.id} className={cn('py-4 whitespace-nowrap', metaClassName ?? 'px-6')}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className='px-6 py-4 text-center text-sm font-normal text-gray-900'>
                  {emptyState ?? 'No results.'}
                </td>
              </tr>
            )}
          </tbody>
          {footerContent && (
            <tfoot className='bg-gray-50 border-t border-gray-200'>
              {footerContent}
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
